// PR-IS-EDIT-3 (Ann 2026-05-24) · Modal "Editar fechas IS".
//
// Permite cambiar fecha_inicio y fecha_fin_prevista de la incidencia activa
// usando los 2 endpoints ya desplegados (PR-IS-EDIT-1/2):
//   - POST /api/suscripciones/{id}/incidencia/preview-editar-fechas  (lectura)
//   - POST /api/suscripciones/{id}/incidencia/editar-fechas          (apply)
//
// Flujo UI obligatorio:
//   1. Inputs date para fecha inicio + fecha final (null = indefinida)
//   2. Botón "Previsualizar cambios" → llama preview
//   3. Muestra editable + meses afectados + cobros intocables + motivos
//   4. Si editable=true → botón "Guardar cambios" → llama editar-fechas
//   5. Si editable=false → bloqueo visible · NO permite guardar
//   6. Si aviso_activacion_pendiente=true → muestra aviso de activación manual
//
// AUTOPISTA_MARKER · NO_USAR_LEGACY: este componente NO toca cobros/facturas/
// remesas/SEPA directamente · solo invoca los 2 endpoints canónicos que
// reutilizan _cobro_es_intocable + recalcular_cobros_por_incidencia.

import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { AlertTriangle, Calendar, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const fmtFecha = (s) => {
  if (!s) return "—";
  try {
    return new Date(String(s).slice(0, 10)).toLocaleDateString("es-ES");
  } catch {
    return s;
  }
};

const fmtEuros = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(v);
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(2)} €`;
};

const ModalEditarFechasIS = ({ open, onClose, suscripcionId, incidenciaActiva, onAplicado }) => {
  const [fechaInicioNueva, setFechaInicioNueva] = useState("");
  const [fechaFinPrevistaNueva, setFechaFinPrevistaNueva] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // PR-IS-EDIT-FIX-1 (Ann 2026-05-24) · estado del preview AUTOMÁTICO
  // inicial · indica si las fechas actuales ya están bloqueadas por economía
  // cerrada (cobros confirmados/facturados/remesados). Si bloqueado, los
  // inputs no se pueden tocar — caso testigo: Luis Toledano IS abr-2026
  // con abril ya facturado.
  const [previewInicial, setPreviewInicial] = useState(null);
  const [previewInicialLoading, setPreviewInicialLoading] = useState(false);

  // Reset al abrir/cerrar con nuevas fechas iniciales + auto-preview
  useEffect(() => {
    if (open && incidenciaActiva) {
      const fi0 = incidenciaActiva.fecha_inicio
        ? String(incidenciaActiva.fecha_inicio).slice(0, 10) : "";
      const ff0 = incidenciaActiva.fecha_fin_prevista
        ? String(incidenciaActiva.fecha_fin_prevista).slice(0, 10) : "";
      setFechaInicioNueva(fi0);
      setFechaFinPrevistaNueva(ff0);
      setPreview(null);
      setErrorMsg("");
      setPreviewInicial(null);
      // PR-IS-EDIT-FIX-1 · preview AUTOMÁTICO al abrir con fechas actuales
      // para detectar si las fechas están bloqueadas por economía cerrada.
      if (fi0 && suscripcionId) {
        (async () => {
          setPreviewInicialLoading(true);
          try {
            const res = await axios.post(
              `${API}/suscripciones/${suscripcionId}/incidencia/preview-editar-fechas`,
              { fecha_inicio_nueva: fi0, fecha_fin_prevista_nueva: ff0 || null }
            );
            setPreviewInicial(res.data);
          } catch (e) {
            // Silencioso · el operador puede intentarlo manualmente
            setPreviewInicial({ editable: true, error_inicial: true });
          } finally {
            setPreviewInicialLoading(false);
          }
        })();
      }
    }
  }, [open, incidenciaActiva, suscripcionId]);

  if (!open) return null;
  const ia = incidenciaActiva || {};

  // PR-IS-EDIT-FIX-1 · flag derivado · true si las fechas actuales están
  // bloqueadas según preview inicial (con FECHAS ACTUALES). Si lo están,
  // inputs disabled para que el operador no pueda ni intentar tocarlos.
  const bloqueadoPorEconomia = !!(previewInicial && previewInicial.editable === false);

  const handlePreview = async () => {
    setErrorMsg("");
    setPreview(null);
    if (!fechaInicioNueva) {
      setErrorMsg("La fecha de inicio es obligatoria.");
      return;
    }
    setPreviewLoading(true);
    try {
      const body = {
        fecha_inicio_nueva: fechaInicioNueva,
        fecha_fin_prevista_nueva: fechaFinPrevistaNueva || null,
      };
      const res = await axios.post(
        `${API}/suscripciones/${suscripcionId}/incidencia/preview-editar-fechas`,
        body
      );
      setPreview(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      const code = detail?.code || "ERROR";
      const msg = typeof detail?.detail === "string"
        ? detail.detail
        : (detail?.detail?.razon || `Error al previsualizar (${code})`);
      setErrorMsg(`${code}: ${msg}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!preview || !preview.editable) return;
    setErrorMsg("");
    setApplyLoading(true);
    try {
      const body = {
        fecha_inicio_nueva: fechaInicioNueva,
        fecha_fin_prevista_nueva: fechaFinPrevistaNueva || null,
      };
      const res = await axios.post(
        `${API}/suscripciones/${suscripcionId}/incidencia/editar-fechas`,
        body
      );
      toast.success("Fechas IS actualizadas correctamente");
      if (typeof onAplicado === "function") onAplicado(res.data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      const code = detail?.code || "ERROR";
      const msg = typeof detail?.detail === "string"
        ? detail.detail
        : (detail?.detail?.razon || `Error al aplicar (${code})`);
      setErrorMsg(`${code}: ${msg}`);
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-700" />
            Editar fechas IS
          </DialogTitle>
          <DialogDescription>
            Cambiar Inicio IS y/o Final IS. El cambio se previsualiza antes de aplicarse
            y se bloquea si afecta a periodos ya cobrados, facturados o remesados.
          </DialogDescription>
        </DialogHeader>

        {/* Datos actuales */}
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
          <div className="font-semibold text-gray-700 mb-1">Datos actuales</div>
          <div>Inicio IS actual: <strong>{fmtFecha(ia.fecha_inicio)}</strong></div>
          <div>Final IS actual: <strong>
            {ia.fecha_fin_prevista ? fmtFecha(ia.fecha_fin_prevista) : "indefinida"}
          </strong></div>
          <div>Precio IS: <strong>{fmtEuros(ia.precio_incidencia)}</strong></div>
          {ia.motivo && <div>Motivo: <strong>{ia.motivo}</strong></div>}
        </div>

        {/* PR-IS-EDIT-FIX-1 · banner bloqueo inicial · si las fechas actuales
            están bloqueadas por economía cerrada, avisar arriba y deshabilitar
            inputs · caso testigo Luis Toledano: IS abril ya facturada */}
        {bloqueadoPorEconomia && (
          <div
            data-testid="efi-bloqueo-inicial"
            className="mt-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900 flex items-start gap-2"
          >
            <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold mb-1">
                Las fechas de esta IS no se pueden modificar.
              </div>
              <div>
                Afectan a periodos ya cobrados, facturados o remesados (cobros
                intocables). La normativa Ann no permite modificar fechas IS
                cuando hay líneas de pago, facturas, remesas o pagos ya
                ejecutados sobre el rango actual.
              </div>
              {(previewInicial?.motivos_bloqueo || []).length > 0 && (
                <ul className="list-disc ml-4 mt-1">
                  {previewInicial.motivos_bloqueo.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Inputs nuevas fechas */}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label htmlFor="efi-fi" className="text-sm">Nueva fecha inicio</Label>
            <input
              id="efi-fi"
              data-testid="efi-fecha-inicio-nueva"
              type="date"
              className={
                "mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm " +
                (bloqueadoPorEconomia ? "bg-gray-100 cursor-not-allowed text-gray-500" : "")
              }
              value={fechaInicioNueva}
              disabled={bloqueadoPorEconomia || previewInicialLoading}
              readOnly={bloqueadoPorEconomia}
              title={bloqueadoPorEconomia
                ? "Bloqueado · esta fecha afecta a cobros ya cerrados"
                : ""}
              onChange={(e) => { setFechaInicioNueva(e.target.value); setPreview(null); }}
            />
          </div>
          <div>
            <Label htmlFor="efi-ff" className="text-sm">Nueva fecha final (opcional)</Label>
            <input
              id="efi-ff"
              data-testid="efi-fecha-fin-nueva"
              type="date"
              className={
                "mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm " +
                (bloqueadoPorEconomia ? "bg-gray-100 cursor-not-allowed text-gray-500" : "")
              }
              value={fechaFinPrevistaNueva}
              disabled={bloqueadoPorEconomia || previewInicialLoading}
              readOnly={bloqueadoPorEconomia}
              title={bloqueadoPorEconomia
                ? "Bloqueado · esta fecha afecta a cobros ya cerrados"
                : ""}
              onChange={(e) => { setFechaFinPrevistaNueva(e.target.value); setPreview(null); }}
            />
            <div className="text-[11px] text-gray-500 mt-1">
              Vacío = IS indefinida
            </div>
          </div>
        </div>

        {/* Botón previsualizar */}
        <div className="mt-2 flex items-center gap-2">
          <Button
            type="button"
            data-testid="efi-previsualizar"
            variant="outline"
            disabled={previewLoading || applyLoading || bloqueadoPorEconomia}
            onClick={handlePreview}
          >
            {previewLoading ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Previsualizando…</>) : "Previsualizar cambios"}
          </Button>
          {preview && (
            <span className="text-xs text-gray-600">
              Previsualización: <strong>{preview.editable ? "Editable" : "Bloqueado"}</strong>
            </span>
          )}
        </div>

        {/* Error de validación */}
        {errorMsg && (
          <div data-testid="efi-error" className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800 flex items-start gap-2">
            <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Preview resultado */}
        {preview && (
          <div data-testid="efi-preview-resultado" className="mt-3 space-y-2">
            {/* Aviso activación pendiente */}
            {preview.aviso_activacion_pendiente && (
              <div data-testid="efi-aviso-activacion" className="rounded border border-yellow-300 bg-yellow-50 p-2 text-xs text-yellow-900 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  La nueva fecha de inicio ya ha llegado o es anterior a hoy. La IS no
                  se activa automáticamente; deberá activarse con la acción correspondiente.
                </span>
              </div>
            )}

            {/* Bloqueo económico */}
            {!preview.editable && (
              <div data-testid="efi-bloqueo-economico" className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900 flex items-start gap-2">
                <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold mb-1">
                    No se pueden modificar estas fechas porque afectan a periodos ya cobrados, facturados o remesados.
                  </div>
                  {(preview.motivos_bloqueo || []).length > 0 && (
                    <ul className="list-disc ml-4 mt-1">
                      {preview.motivos_bloqueo.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* Editable */}
            {preview.editable && (
              <div className="rounded border border-green-300 bg-green-50 p-2 text-xs text-green-900 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Cambio editable. Se recalcularán {preview.rango_nuevo?.meses_recalculables ?? 0} mes(es).
                </span>
              </div>
            )}

            {/* Tabla meses afectados */}
            {(preview.meses_afectados || []).length > 0 && (
              <div className="rounded border border-gray-200 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-[10px]">
                    <tr>
                      <th className="px-2 py-1 text-left">Mes</th>
                      <th className="px-2 py-1 text-left">Acción</th>
                      <th className="px-2 py-1 text-left">Estado cobro</th>
                      <th className="px-2 py-1 text-right">Importe actual</th>
                      <th className="px-2 py-1 text-right">Importe calculado</th>
                      <th className="px-2 py-1 text-left">Motivo intocable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.meses_afectados.map((m, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-2 py-1">{String(m.mes).padStart(2, "0")}/{m.anyo}</td>
                        <td className="px-2 py-1">{m.accion}</td>
                        <td className="px-2 py-1">{m.estado_cobro || "—"}</td>
                        <td className="px-2 py-1 text-right">{fmtEuros(m.importe_actual)}</td>
                        <td className="px-2 py-1 text-right">{fmtEuros(m.importe_calculado)}</td>
                        <td className="px-2 py-1 text-[11px] text-red-700">{m.motivo_intocable || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={applyLoading}
          >
            Cerrar
          </Button>
          <Button
            type="button"
            data-testid="efi-guardar"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!preview || !preview.editable || applyLoading || previewLoading || bloqueadoPorEconomia}
            onClick={handleApply}
          >
            {applyLoading ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Guardando…</>) : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalEditarFechasIS;
